import { render } from 'lit';
import { bugsnagClient } from '@mrhenry/wp--bugsnag-config';
import { MutationFeedback } from './data';
import { renderVisitorRegistrationForm, renderVisitorRegistrationSuccess } from './view';
import '../globals/globals';

const REGISTER_VISITOR_MUTATION = `
	mutation (
		$name: String!,
		$email: String!,
		$contactAboutFutureEditionsConsent: Boolean!,
		$additionalInformation: JSONObject!,
		$language: Language!,
	) {
		registerVisitor(
			input: {
				name: $name,
				email: $email,
				contactAboutFutureEditionsConsent: $contactAboutFutureEditionsConsent,
				additionalInformation: $additionalInformation
			},
			language: $language
		)
	}
`;

type RegistrationResult = {
	success: boolean;
	messages: string[];
}

function fallbackToOtherField( field: HTMLInputElement, otherField: HTMLInputElement ) {
	if ( field.value !== 'Andere' ) {
		return field.value.trim();
	}

	if ( otherField.value.trim() ) {
		return otherField.value.trim();
	}

	return 'Andere';
}

function visitorRegistrationForm( parent: HTMLElement ) {

	async function handleSubmit( event: Event ) {
		event.preventDefault();
		event.stopPropagation();

		const form = event.target as HTMLFormElement;
		if ( !form ) {
			return;
		}

		const result = await submitData(
			form.firstName.value,
			form.lastName.value,
			form.email.value,
			form.studentNumber.value,
			fallbackToOtherField( form.school, form.schoolOther ),
			fallbackToOtherField( form.campus, form.campusOther ),
			fallbackToOtherField( form.academicYear, form.academicYearOther ),
			fallbackToOtherField( form.studyArea, form.studyAreaOther ),
			form.education.value,
			form.communicationOptIn.checked
		);

		if ( result && !result.success ) {
			const withErrors = result.messages;

			render(
				renderVisitorRegistrationForm( parent, handleSubmit, withErrors ),
				parent
			);

			return;
		}

		render(
			renderVisitorRegistrationSuccess(),
			parent
		);

		document.getElementById( 'visitor-registration-form' )?.scrollIntoView( {
			block: 'start',
		} );

		return;
	}

	async function submitData(
		firstName: string,
		lastName: string,
		email: string,
		studentNumber: string,
		school: string,
		campus: string,
		academicYear: string,
		studyArea: string,
		education: string,
		communicationOptIn: boolean
	): Promise<RegistrationResult> {

		try {
			const endpoint = new URL( '/graphql', 'https://' + window.Mr.study360ApiDomain );
			const response = await fetch( endpoint, {
				credentials: 'include',
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify( {
					query: REGISTER_VISITOR_MUTATION,
					variables: {
						name: firstName + ' ' + lastName,
						email: email,
						contactAboutFutureEditionsConsent: communicationOptIn,
						additionalInformation: {
							studentNumber: studentNumber,
							school: school,
							campus: campus,
							academicYear: academicYear,
							studyArea: studyArea,
							education: education,
						},
						language: document.documentElement.getAttribute( 'lang' )?.toUpperCase() || 'NL',
					},
				} ),
			} );

			if ( !response.ok ) {
				bugsnagClient.notify( new Error( `[study360 signup student] API Error: ${response.status}: ${response.statusText}` ) );

				return {
					success: false,
					messages: [
						window.translations.errorMessages.generic,
					],
				};
			}

			const responseBody = await response.json();

			if ( responseBody.data?.registerVisitor === MutationFeedback.OK ) {
				return {
					success: true,
					messages: [],
				};
			}

			bugsnagClient.notify( new Error( `[study360 signup student] API Error: ${responseBody}` ) );

			const messages: string[] = [];
			responseBody.errors?.forEach( ( message:string ) => {
				messages.push( message );
			} );

			return {
				success: false,
				messages: messages,
			};
		} catch ( err ) {
			bugsnagClient.notify( err );

			return {
				success: false,
				messages: [
					window.translations.errorMessages.generic,
					err,
				],
			};
		}
	}

	render(
		renderVisitorRegistrationForm( parent, handleSubmit, [] ),
		parent
	);
}

class MrVisitorRegistrationForm extends HTMLElement {
	connectedCallback() {
		requestAnimationFrame( () => {
			while ( this.firstChild ) {
				this.removeChild( this.lastChild! );
			}

			visitorRegistrationForm( this );
		} );
	}
}

customElements.define( 'mr-visitor-registration-form', MrVisitorRegistrationForm );
